import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, TrendingUp, Trophy, XCircle, PlusCircle, Clock, Minus, Filter } from 'lucide-react';
import { apiGet } from '../data/api';
import PlaceBetModal from '../components/PlaceBetModal';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

function fmt(v, dec = 2) { return v > 0 ? v.toFixed(dec) : '—'; }
function fmtPct(v) { return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '—'; }

const COLUMNS = [
  { key: 'match', label: 'Match', align: 'left', sortable: true },
  { key: 'tournament', label: 'Torneo', align: 'left', sortable: true },
  { key: 'datetime', label: 'Data/Ora', align: 'center', sortable: true },
  { key: 'odd_p1', label: 'Quota P1', align: 'center', sortable: true },
  { key: 'odd_p2', label: 'Quota P2', align: 'center', sortable: true },
  { key: 'fair_p1', label: 'Fair P1', align: 'center', sortable: true },
  { key: 'fair_p2', label: 'Fair P2', align: 'center', sortable: true },
  { key: 'ev', label: 'EV', align: 'right', sortable: true },
  { key: 'stake', label: 'Giocata', align: 'right', sortable: true },
  { key: 'conf', label: 'Conf.', align: 'right', sortable: true },
  { key: 'action', label: '', align: 'center', sortable: false },
];

function getBestRec(recs) {
  if (!recs || !recs.length) return null;
  return recs.reduce((best, r) =>
    (r.recommended_stake_rounded || 0) > (best.recommended_stake_rounded || 0) ? r : best
  , recs[0]);
}

function getRecMarketTypes(recs) {
  if (!recs) return [];
  const types = new Set();
  for (const r of recs) {
    if (r.recommended_stake_rounded > 0) types.add(r.market_type);
  }
  return Array.from(types);
}

const MARKET_LABELS = { match_winner: 'MW', over_under: 'O/U', handicap: 'HC' };
const MARKET_COLORS = {
  match_winner: 'bg-blue-500/15 text-blue-400',
  over_under: 'bg-amber-500/15 text-amber-400',
  handicap: 'bg-purple-500/15 text-purple-400',
};
const KELLY_LABELS = { full: 'Full Kelly', half: 'Half Kelly', quarter: 'Quarter Kelly' };

function extractSortValue(m, key) {
  const mo = m.model_output || {};
  const odds = m.latest_odds || {};
  switch (key) {
    case 'match': return (m.player1_name || '').toLowerCase();
    case 'tournament': return (m.tournament_name || '').toLowerCase();
    case 'datetime': return m.match_datetime || '';
    case 'odd_p1': return odds.odd_p1_decimal || 0;
    case 'odd_p2': return odds.odd_p2_decimal || 0;
    case 'fair_p1': return mo.fair_odd_p1 || 0;
    case 'fair_p2': return mo.fair_odd_p2 || 0;
    case 'ev': {
      const evMw = Math.max(mo.ev_p1_pct || -999, mo.ev_p2_pct || -999);
      const recs = m.recommendations || [];
      const evRecs = recs.map(r => r.ev_pct || -999);
      return Math.max(evMw, ...evRecs);
    }
    case 'stake': {
      const best = getBestRec(m.recommendations);
      return best ? best.recommended_stake_rounded : 0;
    }
    case 'conf': return mo.confidence_score || 0;
    default: return 0;
  }
}

function ConfBadge({ score }) {
  const pct = Math.round(score * 100);
  const cls = pct >= 60 ? 'bg-emerald-500/15 text-emerald-400' :
              pct >= 30 ? 'bg-amber-500/15 text-amber-400' :
                          'bg-zinc-500/15 text-zinc-400';
  return <span className={`px-1.5 py-0.5 text-xs rounded font-mono ${cls}`}>{pct}%</span>;
}

/* ─── Value Results View (Finiti tab) ─── */
function _today() {
  return new Date().toISOString().slice(0, 10);
}
function _daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function TournamentDropdown({ tournaments, value, onChange, testId }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      data-testid={testId || 'tournament-filter'}
      className="bg-zinc-800/50 border border-zinc-700/50 rounded-sm px-2.5 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-emerald-500/50 max-w-[250px] truncate appearance-none cursor-pointer"
      style={mono}
    >
      <option value="">Tutti i tornei</option>
      {tournaments.map(t => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
  );
}

function ValueResultsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minConf, setMinConf] = useState(0);
  const [marketFilter, setMarketFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tournamentFilter, setTournamentFilter] = useState('');

  useEffect(() => {
    apiGet('/tt/value-results')
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const bets = data?.bets || [];

  const tournaments = useMemo(() => {
    const set = new Set(bets.map(b => b.tournament).filter(Boolean));
    return Array.from(set).sort();
  }, [bets]);

  // Filter bets by date, confidence, tournament, and market type
  const filtered = useMemo(() => {
    return bets
      .filter(b => {
        if (dateFrom && b.match_datetime && b.match_datetime.slice(0, 10) < dateFrom) return false;
        if (dateTo && b.match_datetime && b.match_datetime.slice(0, 10) > dateTo) return false;
        if (tournamentFilter && b.tournament !== tournamentFilter) return false;
        return true;
      })
      .filter(b => Math.round(b.confidence * 100) >= minConf)
      .map(b => {
        if (marketFilter === 'all') return b;
        return {
          ...b,
          mw_bet: marketFilter === 'mw' ? b.mw_bet : null,
          ou_bets: marketFilter === 'ou' ? (b.ou_bets || []) : [],
          hc_bets: marketFilter === 'hc' ? (b.hc_bets || []) : [],
        };
      })
      .filter(b => b.mw_bet || (b.ou_bets && b.ou_bets.length) || (b.hc_bets && b.hc_bets.length));
  }, [bets, minConf, marketFilter, dateFrom, dateTo, tournamentFilter]);

  // Recalculate stats on filtered bets
  const stats = useMemo(() => {
    let wins = 0, losses = 0, profit = 0, staked = 0;
    for (const b of filtered) {
      if (b.mw_bet) {
        staked += 1; profit += b.mw_bet.profit;
        if (b.mw_bet.won) wins++; else losses++;
      }
      for (const ou of (b.ou_bets || [])) {
        staked += 1; profit += ou.profit;
        if (ou.won) wins++; else losses++;
      }
      for (const hc of (b.hc_bets || [])) {
        staked += 1; profit += hc.profit;
        if (hc.won) wins++; else losses++;
      }
    }
    const total = wins + losses;
    return {
      total_bets: total, wins, losses,
      total_profit: Math.round(profit * 100) / 100,
      total_staked: staked,
      win_rate: total > 0 ? Math.round(wins / total * 1000) / 10 : 0,
      roi: staked > 0 ? Math.round(profit / staked * 1000) / 10 : 0,
    };
  }, [filtered]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-500" size={24} /></div>;
  if (bets.length === 0) return <div className="text-center py-20 text-zinc-500 text-sm">Nessuna giocata EV+ conclusa</div>;

  const confSteps = [0, 20, 30, 40, 50, 60, 70, 80];
  const marketOpts = [
    { key: 'all', label: 'Tutti' },
    { key: 'mw', label: 'Match Winner' },
    { key: 'ou', label: 'Over/Under' },
    { key: 'hc', label: 'Handicap' },
  ];

  return (
    <div className="space-y-4" data-testid="value-results-view">
      {/* Filters */}
      <div className="rounded-sm p-3 flex flex-col gap-3" style={card}>
        {/* Market filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs text-zinc-500 uppercase tracking-wider w-28">Mercato</span>
          <div className="flex gap-1.5">
            {marketOpts.map(m => (
              <button
                key={m.key}
                data-testid={`market-filter-${m.key}`}
                onClick={() => setMarketFilter(m.key)}
                className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                  marketFilter === m.key
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        {/* Tournament filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs text-zinc-500 uppercase tracking-wider w-28">Torneo</span>
          <TournamentDropdown
            tournaments={tournaments}
            value={tournamentFilter}
            onChange={setTournamentFilter}
            testId="finiti-tournament-filter"
          />
          {tournamentFilter && (
            <button
              onClick={() => setTournamentFilter('')}
              className="px-2 py-1 text-[10px] rounded-sm bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300 transition-colors"
              data-testid="finiti-tournament-clear"
            >
              Reset
            </button>
          )}
        </div>
        {/* Confidence filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs text-zinc-500 uppercase tracking-wider w-28">Confidence min.</span>
          <div className="flex gap-1.5">
            {confSteps.map(c => (
              <button
                key={c}
                data-testid={`conf-filter-${c}`}
                onClick={() => setMinConf(c)}
                className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                  minConf === c
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300'
                }`}
                style={mono}
              >
                {c === 0 ? 'Tutte' : `${c}%+`}
              </button>
            ))}
          </div>
        </div>
        {/* Date filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs text-zinc-500 uppercase tracking-wider w-28">Periodo</span>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Quick presets */}
            <div className="flex gap-1.5 flex-wrap">
              {[
                { key: 'all', label: 'Tutto' },
                { key: 'today', label: 'Oggi' },
                { key: '3d', label: '3gg' },
                { key: '7d', label: '7gg' },
                { key: '30d', label: '30gg' },
              ].map(p => {
                const isActive =
                  (p.key === 'all' && !dateFrom && !dateTo) ||
                  (p.key === 'today' && dateFrom === _today() && dateTo === _today()) ||
                  (p.key === '3d' && dateFrom === _daysAgo(3) && dateTo === _today()) ||
                  (p.key === '7d' && dateFrom === _daysAgo(7) && dateTo === _today()) ||
                  (p.key === '30d' && dateFrom === _daysAgo(30) && dateTo === _today());
                return (
                  <button
                    key={p.key}
                    data-testid={`period-${p.key}`}
                    onClick={() => {
                      if (p.key === 'all') { setDateFrom(''); setDateTo(''); }
                      else if (p.key === 'today') { setDateFrom(_today()); setDateTo(_today()); }
                      else if (p.key === '3d') { setDateFrom(_daysAgo(3)); setDateTo(_today()); }
                      else if (p.key === '7d') { setDateFrom(_daysAgo(7)); setDateTo(_today()); }
                      else if (p.key === '30d') { setDateFrom(_daysAgo(30)); setDateTo(_today()); }
                    }}
                    className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${
                      isActive
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {/* Manual date range */}
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                data-testid="date-from"
                className="bg-zinc-800/50 border border-zinc-700/50 rounded-sm px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-emerald-500/50 min-w-[130px]"
                style={mono}
              />
              <span className="text-zinc-600 text-xs">—</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                data-testid="date-to"
                className="bg-zinc-800/50 border border-zinc-700/50 rounded-sm px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-emerald-500/50 min-w-[130px]"
                style={mono}
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  data-testid="date-clear"
                  className="px-2 py-1 text-[10px] rounded-sm bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300 transition-colors"
                >
                  Reset
                </button>
              )}
              <span className="text-xs text-zinc-600 ml-auto" style={mono}>
                {filtered.length} / {bets.length} match
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Giocate" value={stats.total_bets} testId="stat-total" />
        <StatCard
          label="Win Rate"
          value={`${stats.win_rate}%`}
          color={stats.win_rate >= 50 ? 'emerald' : 'rose'}
          testId="stat-winrate"
        />
        <StatCard
          label="ROI"
          value={`${stats.roi > 0 ? '+' : ''}${stats.roi}%`}
          color={stats.roi >= 0 ? 'emerald' : 'rose'}
          testId="stat-roi"
        />
        <StatCard
          label="P/L (unita)"
          value={`${stats.total_profit > 0 ? '+' : ''}${stats.total_profit.toFixed(2)}`}
          color={stats.total_profit >= 0 ? 'emerald' : 'rose'}
          testId="stat-pl"
        />
        <StatCard
          label="W / L"
          value={`${stats.wins} / ${stats.losses}`}
          testId="stat-wl"
        />
      </div>

      {/* Bets List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm">Nessuna giocata con Confidence ≥ {minConf}%</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((b, i) => (
            <BetCard key={i} bet={b} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, testId }) {
  const colorCls = color === 'emerald' ? 'text-emerald-400' :
                   color === 'rose' ? 'text-rose-400' :
                   'text-zinc-200';
  return (
    <div className="rounded-sm p-3" style={card} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${colorCls}`} style={mono}>{value}</div>
    </div>
  );
}

function BetCard({ bet, index }) {
  const b = bet;
  // Determine overall win/loss from all bets in this match
  const allBets = [
    ...(b.mw_bet ? [b.mw_bet] : []),
    ...(b.ou_bets || []),
    ...(b.hc_bets || []),
  ];
  const totalProfit = allBets.reduce((s, x) => s + x.profit, 0);
  const wonColor = totalProfit >= 0 ? 'border-l-emerald-500' : 'border-l-rose-500';

  return (
    <div
      className={`rounded-sm border-l-[3px] ${wonColor} overflow-hidden`}
      style={{ background: '#18181B', border: '1px solid #27272A', borderLeftWidth: '3px' }}
      data-testid={`bet-card-${index}`}
    >
      <div className="p-4">
        {/* Top row: match info + result */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-zinc-200">{b.player1}</span>
              <span className="text-zinc-600 text-xs">vs</span>
              <span className="text-sm font-medium text-zinc-200">{b.player2}</span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[10px] text-zinc-600">{b.tournament}</span>
              <span className="text-[10px] text-zinc-600">
                {b.match_datetime ? new Date(b.match_datetime).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-sm font-bold text-zinc-300" style={mono} data-testid={`bet-result-${index}`}>
              {b.result}
            </div>
            {b.set_scores && b.set_scores.length > 0 && (
              <div className="text-[10px] text-zinc-600 mt-0.5" style={mono}>
                {b.set_scores.map(s => `${s[0]}-${s[1]}`).join(' | ')}
              </div>
            )}
            {b.total_points != null && (
              <div className="text-[10px] text-zinc-500 mt-0.5">Tot: <span style={mono}>{b.total_points} pts</span></div>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          {/* Match Winner Bet */}
          {b.mw_bet && (
            <BetRow
              won={b.mw_bet.won}
              label={`${b.mw_bet.value_player} @ ${b.mw_bet.value_odd.toFixed(2)}`}
              tag="MW"
              ev={b.mw_bet.value_ev}
              profit={b.mw_bet.profit}
              testId={`bet-mw-${index}`}
            />
          )}

          {/* O/U Bets */}
          {(b.ou_bets || []).map((ou, j) => (
            <BetRow
              key={`ou-${j}`}
              won={ou.won}
              label={`${ou.side === 'over' ? 'Over' : 'Under'} ${ou.line} @ ${ou.odd.toFixed(2)}`}
              tag="O/U"
              ev={ou.ev}
              extra={`Tot: ${ou.total_points} pts`}
              profit={ou.profit}
              testId={`bet-ou-${index}-${j}`}
            />
          ))}

          {/* Handicap Bets */}
          {(b.hc_bets || []).map((hc, j) => (
            <BetRow
              key={`hc-${j}`}
              won={hc.won}
              label={`${hc.label} @ ${hc.odd.toFixed(2)}`}
              tag="HC"
              ev={hc.ev}
              extra={`Set: ${hc.result_sets}`}
              profit={hc.profit}
              testId={`bet-hc-${index}-${j}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function BetRow({ won, label, tag, ev, evLabel = 'EV', extra, profit, testId }) {
  return (
    <div className={`flex items-center gap-2.5 rounded-sm px-3 py-2.5 ${won ? 'bg-emerald-500/[0.06]' : 'bg-rose-500/[0.04]'}`}
      data-testid={testId}>
      {won ? <Trophy size={14} className="text-emerald-400 flex-shrink-0" /> : <XCircle size={14} className="text-rose-400 flex-shrink-0" />}
      <span className={`text-xs font-bold w-9 ${won ? 'text-emerald-400' : 'text-rose-400'}`}>{won ? 'WIN' : 'LOSS'}</span>
      <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-zinc-800 text-zinc-400">{tag}</span>
      <span className="text-xs text-zinc-400">{label}</span>
      <span className="text-[10px] text-zinc-600">{evLabel} {fmtPct(ev)}</span>
      {extra && <span className="text-[10px] text-zinc-500">{extra}</span>}
      <span className={`ml-auto text-sm font-bold flex-shrink-0 ${profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} style={mono}>
        {profit >= 0 ? '+' : ''}{profit.toFixed(2)}u
      </span>
    </div>
  );
}


const BET_STATUS_CONFIG = {
  pending: { label: 'Pending', cls: 'bg-amber-500/15 text-amber-400', icon: Clock },
  won: { label: 'Vinta', cls: 'bg-emerald-500/15 text-emerald-400', icon: Trophy },
  lost: { label: 'Persa', cls: 'bg-rose-500/15 text-rose-400', icon: XCircle },
  void: { label: 'Void', cls: 'bg-zinc-500/15 text-zinc-400', icon: Minus },
};

function BetStatusBadge({ bets }) {
  if (!bets || !bets.length) return null;
  // Show the most relevant status
  const statuses = bets.map(b => b.result_status);
  const display = statuses.includes('pending') ? 'pending' :
                  statuses.includes('won') ? 'won' :
                  statuses.includes('lost') ? 'lost' : 'void';
  const cfg = BET_STATUS_CONFIG[display];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-bold rounded ${cfg.cls}`} data-testid="bet-status-badge">
      <Icon size={9} />
      {cfg.label}
    </span>
  );
}

/* ─── Prematch / Live Table ─── */
function MatchTable({ matches, betStatusMap, onPlaceBet }) {
  const [sortKey, setSortKey] = useState('ev');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!sortKey) return matches;
    return [...matches].sort((a, b) => {
      const va = extractSortValue(a, sortKey);
      const vb = extractSortValue(b, sortKey);
      if (typeof va === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [matches, sortKey, sortDir]);

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (sorted.length === 0) return <div className="text-center py-20 text-zinc-500 text-sm">Nessun match</div>;

  return (
    <div className="rounded-sm overflow-x-auto" style={card}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-zinc-500 uppercase tracking-wider" style={{ borderBottom: '1px solid #27272A' }}>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                data-testid={`sort-${col.key}`}
                onClick={() => col.sortable && handleSort(col.key)}
                className={`px-3 py-3 font-medium whitespace-nowrap ${
                  col.align === 'left' ? 'text-left' : col.align === 'right' ? 'text-right' : 'text-center'
                } ${col.key === 'match' ? 'px-5' : ''} ${col.sortable ? 'cursor-pointer select-none hover:text-zinc-300 transition-colors' : ''}`}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {col.sortable && (
                    sortKey === col.key
                      ? (sortDir === 'asc' ? <ChevronUp size={12} className="text-emerald-400" /> : <ChevronDown size={12} className="text-emerald-400" />)
                      : <ChevronsUpDown size={12} className="opacity-30" />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(m => {
            const mo = m.model_output || {};
            const odds = m.latest_odds || {};
            const mwEv = Math.max(mo.ev_p1_pct || 0, mo.ev_p2_pct || 0);
            const bestSide = (mo.ev_p1_pct || 0) >= (mo.ev_p2_pct || 0) ? 1 : 2;
            const bestRec = getBestRec(m.recommendations);
            const recMarkets = getRecMarketTypes(m.recommendations);

            // Best EV across all markets (MW, HC, O/U)
            const recsEvs = (m.recommendations || [])
              .filter(r => r.recommended_stake_rounded > 0)
              .map(r => ({ ev: r.ev_pct || 0, market: r.market_type }));
            const mwEntry = mwEv > 0 ? { ev: mwEv, market: 'match_winner' } : null;
            const allEvs = [...recsEvs, ...(mwEntry ? [mwEntry] : [])];
            const bestEvEntry = allEvs.length > 0
              ? allEvs.reduce((best, e) => e.ev > best.ev ? e : best, allEvs[0])
              : null;
            const bestEv = bestEvEntry ? bestEvEntry.ev : Math.max(mo.ev_p1_pct || -99, mo.ev_p2_pct || -99);
            const bestEvMarket = bestEvEntry ? bestEvEntry.market : null;

            const agreeStatus = m.models_agree;

            return (
              <tr key={m.id} data-testid={`match-row-${m.id}`}
                className={`border-t border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${agreeStatus === 'agree' ? 'border-l-2 border-l-emerald-500 bg-emerald-500/5' : agreeStatus === 'diverge' ? 'border-l-2 border-l-red-500/40 bg-red-500/5' : ''}`}>
                <td className="px-5 py-3">
                  <div className="text-zinc-200 font-medium">
                    {m.player1_name} <span className="text-zinc-600">vs</span> {m.player2_name}
                  </div>
                  {agreeStatus === 'agree' && <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold">LR+PASCAL</span>}
                  {agreeStatus === 'diverge' && <span className="text-[8px] px-1 py-0.5 rounded bg-red-500/15 text-red-400 font-bold">DIVERGENZA</span>}
                </td>
                <td className="px-3 py-3 text-xs text-zinc-400 max-w-[120px] truncate">{m.tournament_name}</td>
                <td className="px-3 py-3 text-center text-xs text-zinc-500 whitespace-nowrap">
                  {m.match_datetime ? new Date(m.match_datetime).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                </td>
                <td className="px-3 py-3 text-center font-mono text-zinc-300">
                  <span className={bestSide === 1 && mwEv > 0 ? 'text-emerald-400 font-semibold' : ''}>
                    {fmt(odds.odd_p1_decimal)}
                  </span>
                </td>
                <td className="px-3 py-3 text-center font-mono text-zinc-300">
                  <span className={bestSide === 2 && mwEv > 0 ? 'text-emerald-400 font-semibold' : ''}>
                    {fmt(odds.odd_p2_decimal)}
                  </span>
                </td>
                <td className="px-3 py-3 text-center font-mono text-zinc-400">{fmt(mo.fair_odd_p1)}</td>
                <td className="px-3 py-3 text-center font-mono text-zinc-400">{fmt(mo.fair_odd_p2)}</td>
                <td className="px-3 py-3 text-right">
                  {bestEv > -90 ? (
                    <>
                      <span className={`font-mono text-xs font-semibold ${bestEv > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {fmtPct(bestEv)}
                      </span>
                      {bestEvMarket && bestEvMarket !== 'match_winner' && bestEv > 0 && (
                        <div className="mt-0.5">
                          <span className={`px-1 py-0 text-[7px] font-bold rounded ${MARKET_COLORS[bestEvMarket] || 'bg-zinc-800 text-zinc-500'}`}>
                            {MARKET_LABELS[bestEvMarket] || bestEvMarket}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-zinc-600">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right" data-testid={`stake-cell-${m.id}`}>
                  {bestRec && bestRec.recommended_stake_rounded > 0 ? (
                    <div>
                      <div className="text-sm font-bold text-emerald-400" style={mono}>
                        {bestRec.recommended_stake_rounded.toFixed(2)}€
                      </div>
                      <div className="text-[9px] text-zinc-500 mt-0.5">
                        {KELLY_LABELS[bestRec.recommended_kelly_mode] || bestRec.recommended_kelly_mode}
                      </div>
                      {recMarkets.length > 0 && (
                        <div className="flex gap-0.5 justify-end mt-0.5">
                          {recMarkets.map(mt => (
                            <span key={mt} className={`px-1 py-0 text-[8px] font-bold rounded ${MARKET_COLORS[mt] || 'bg-zinc-800 text-zinc-500'}`}>
                              {MARKET_LABELS[mt] || mt}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-600">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <ConfBadge score={mo.confidence_score || 0} />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    {betStatusMap[m.id] ? (
                      <BetStatusBadge bets={betStatusMap[m.id]} />
                    ) : bestRec && bestRec.recommended_stake_rounded > 0 ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onPlaceBet(m, bestRec); }}
                        data-testid={`place-bet-${m.id}`}
                        className="p-1 rounded-sm text-zinc-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                        title="Segna come giocata"
                      >
                        <PlusCircle size={14} />
                      </button>
                    ) : null}
                    <Link to={`/tt/matches/${encodeURIComponent(m.id)}`} data-testid={`detail-${m.id}`}
                      className="text-zinc-500 hover:text-emerald-400">
                      <ChevronRight size={14} />
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


/* ─── Main Page ─── */
export default function TTMatches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('scheduled');
  const [betStatusMap, setBetStatusMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMatch, setModalMatch] = useState(null);
  const [modalRec, setModalRec] = useState(null);
  const [tournamentFilter, setTournamentFilter] = useState('');
  // Most upcoming matches don't have bookmaker odds yet — the scraper
  // prioritises matches kicking off in the next 2-3h. This filter lets
  // the user hide rows without odds so the table only shows actionable
  // matches.
  const [onlyWithOdds, setOnlyWithOdds] = useState(false);

  const loadMatches = useCallback(() => {
    if (statusFilter === 'value-results') return;
    setLoading(true);
    apiGet(`/tt/matches?status=${statusFilter}`)
      .then(data => {
        setMatches(data);
        // Fetch bet status for all match IDs
        const ids = data.map(m => m.id).join(',');
        if (ids) {
          apiGet(`/tt/match-bet-status?match_ids=${encodeURIComponent(ids)}`)
            .then(setBetStatusMap)
            .catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { loadMatches(); }, [loadMatches]);
  // Reset tournament filter when switching tabs
  useEffect(() => { setTournamentFilter(''); }, [statusFilter]);

  const tournaments = useMemo(() => {
    const set = new Set(matches.map(m => m.tournament_name).filter(Boolean));
    return Array.from(set).sort();
  }, [matches]);

  const filteredMatches = useMemo(() => {
    let arr = matches;
    if (tournamentFilter) {
      arr = arr.filter(m => m.tournament_name === tournamentFilter);
    }
    if (onlyWithOdds) {
      arr = arr.filter(m => m.latest_odds && m.latest_odds.odd_p1_decimal);
    }
    return arr;
  }, [matches, tournamentFilter, onlyWithOdds]);

  // Counter of matches that already have bookmaker odds — surfaced to the
  // operator so it's clear that "—" rows are normal (scraper coverage
  // is time-prioritised) and not a bug.
  const oddsStats = useMemo(() => {
    const base = tournamentFilter
      ? matches.filter(m => m.tournament_name === tournamentFilter)
      : matches;
    const withOdds = base.filter(
      m => m.latest_odds && m.latest_odds.odd_p1_decimal,
    ).length;
    return { withOdds, total: base.length };
  }, [matches, tournamentFilter]);

  const handlePlaceBet = (match, rec) => {
    setModalMatch(match);
    setModalRec(rec);
    setModalOpen(true);
  };

  const handleBetSuccess = () => {
    loadMatches(); // Refresh to update badges
  };

  const tabs = [
    { key: 'scheduled', label: 'Prematch' },
    { key: 'live', label: 'Live' },
    { key: 'value-results', label: 'Finiti', icon: TrendingUp },
  ];

  return (
    <div data-testid="tt-matches-page" className="space-y-5 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-50" style={heading}>
          Match Table Tennis
        </h1>
        <div className="flex gap-1.5">
          {tabs.map(t => (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => setStatusFilter(t.key)}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors flex items-center gap-1.5 ${
                statusFilter === t.key
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300'
              }`}
            >
              {t.icon && <t.icon size={12} />}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tournament filter for Prematch/Live tabs */}
      {statusFilter !== 'value-results' && tournaments.length > 1 && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="main-tournament-filter-bar">
          <Filter size={14} className="text-zinc-500" />
          <TournamentDropdown
            tournaments={tournaments}
            value={tournamentFilter}
            onChange={setTournamentFilter}
            testId="main-tournament-filter"
          />
          {tournamentFilter && (
            <button
              onClick={() => setTournamentFilter('')}
              className="px-2 py-1 text-[10px] rounded-sm bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300 transition-colors"
              data-testid="main-tournament-clear"
            >
              Reset
            </button>
          )}
          <label
            data-testid="only-with-odds-toggle"
            className="flex items-center gap-1.5 px-2 py-1 text-[10px] rounded-sm bg-zinc-800/50 text-zinc-400 border border-zinc-700/50 hover:text-zinc-200 transition-colors cursor-pointer"
            title="Lo scraper Bet365 prioritizza i match che iniziano entro 2-3h, quindi i match più lontani spesso non hanno ancora quote."
          >
            <input
              type="checkbox"
              checked={onlyWithOdds}
              onChange={(e) => setOnlyWithOdds(e.target.checked)}
              className="accent-emerald-500"
            />
            Solo con quote
          </label>
          <span data-testid="odds-coverage-counter"
                className="text-[10px] text-zinc-500 ml-auto" style={mono}>
            {filteredMatches.length} / {matches.length} mostrati ·{' '}
            <span className={
              oddsStats.total === 0 ? 'text-zinc-600'
              : oddsStats.withOdds / oddsStats.total >= 0.5 ? 'text-emerald-400'
              : oddsStats.withOdds / oddsStats.total >= 0.2 ? 'text-amber-400'
              : 'text-rose-400'
            }>
              {oddsStats.withOdds} con quote
            </span>
            {oddsStats.total > 0 && (
              <span className="text-zinc-600">
                {' '}({Math.round(100 * oddsStats.withOdds / oddsStats.total)}%)
              </span>
            )}
          </span>
        </div>
      )}

      {statusFilter === 'value-results' ? (
        <ValueResultsView />
      ) : loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-500" size={24} /></div>
      ) : (
        <>
          {/* Coverage notice — explains why so many rows show "—" in the
             Quota P1/P2 columns. The user reading the table for the first
             time invariably asks "perché tutte queste partite senza
             quote?" — this banner answers preemptively. */}
          {!onlyWithOdds && oddsStats.total > 0
            && oddsStats.withOdds / oddsStats.total < 0.5 && (
            <div data-testid="odds-coverage-notice"
                 className="rounded-sm p-3 text-[11px] leading-relaxed flex items-start gap-2"
                 style={{
                   border: '1px solid rgba(245,158,11,0.30)',
                   background: 'rgba(245,158,11,0.05)',
                   color: '#FCD34D', ...mono,
                 }}>
              <Clock size={12} className="mt-0.5 flex-shrink-0" />
              <div>
                Solo <span className="text-zinc-100 font-semibold">
                {oddsStats.withOdds}/{oddsStats.total}</span> match upcoming
                hanno quote bookmaker. <span className="text-zinc-300">È normale</span>:
                lo scraper Bet365 prioritizza i match che iniziano entro 2-3h.
                Le quote degli altri match arriveranno man mano che si avvicinano al kickoff.
                <span className="ml-2 text-zinc-500">
                  → spunta "Solo con quote" sopra per nascondere le righe vuote.
                </span>
              </div>
            </div>
          )}
          <MatchTable matches={filteredMatches} betStatusMap={betStatusMap} onPlaceBet={handlePlaceBet} />
        </>
      )}

      <PlaceBetModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleBetSuccess}
        matchId={modalMatch?.id}
        recommendation={modalRec}
        matchInfo={modalMatch ? {
          player1: modalMatch.player1_name,
          player2: modalMatch.player2_name,
          tournament: modalMatch.tournament_name,
        } : null}
      />
    </div>
  );
}
